import { autorun, reaction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import { RosterStore } from '../../stores/roster/roster.store';
import { BaseButton } from '../base/button/button';
import type { ICommonDialogContext } from '../base/dialog/dialog';
import { RosterClaimsDialog } from './roster-claims-dialog';
import { RosterDialogContext } from './roster-dialog';
import { RosterDrawer } from './roster-drawer';
import { RosterFilters } from './roster-filters';
import { RosterHeader } from './roster-header';
import * as S from './roster-page.styles';
import { createRosterPresenter, rosterTableFilters, rosterVisibleColumns } from './roster-presenter';
import { RosterTable } from './roster-table';

export interface IRosterScreenProps {
    /** The app's roster store: its data source (support, provider app or mock) and role. */
    store: RosterStore;
    /** The app's dialogs, bridged with `useCommonDialogContext`. */
    dialog: ICommonDialogContext;
}

/**
 * The Roster screen (FE-109): providers and facilities on file for claim submission.
 *
 * Two table presenters, one per tab, each remembering its own sort, page size, columns and
 * selection. The page keeps them in step with the store: rows and total flow store → table, and
 * the store's search and type are mirrored into the active table's filters, which is what sends
 * the table back to page one and makes it ask for data.
 */
export const RosterScreen = observer(({ store, dialog }: IRosterScreenProps): React.ReactElement => {
    const presenters = useMemo(() => {
        const open = (id: string): void => void store.openRecord(id);
        return {
            providers: createRosterPresenter('providers', store.role, store.dataSource, open),
            facilities: createRosterPresenter('facilities', store.role, store.dataSource, open),
        };
    }, [store]);
    const presenter = presenters[store.tab];

    useEffect(() => {
        void store.loadCounts();

        const disposers = [
            autorun(() => {
                const active = presenters[store.tab];
                active.onDataUpdate(store.rows);
                active.onDataTotalChange(store.total ?? 0);
            }),
            reaction(
                () => rosterTableFilters(store),
                filters => presenters[store.tab].updateFilters(filters),
                { fireImmediately: true, equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
            ),
            // The design clears the selection when the type filter or the tab changes.
            reaction(
                () => [store.tab, store.providerType],
                () => Object.values(presenters).forEach(p => p.onSelectedUpdate({})),
            ),
        ];
        return () => disposers.forEach(dispose => dispose());
    }, [store, presenters]);

    return (
        <RosterDialogContext.Provider value={dialog}>
            <S.Wrapper>
                {store.isMock && (
                    // NS-4: generated records must never pass for real ones. Shown for as long as the mock is the source.
                    <S.PageBanner tone="warning" title="Mock data — not connected to the API">
                        Every provider, facility and claim on this page is generated. Changes are kept only until the
                        page is reloaded.
                    </S.PageBanner>
                )}
                <RosterHeader
                    store={store}
                    getVisibleColumns={() => rosterVisibleColumns(presenter, store.tab, store.role, store.dataSource)}
                />
                <RosterFilters store={store} />
                {store.error && (
                    <S.PageBanner
                        tone="danger"
                        title={`${store.tab === 'providers' ? 'Providers' : 'Facilities'} could not be loaded`}
                        action={
                            <BaseButton
                                variant="outlined"
                                size="small"
                                // The list only — not the counts. A failing count request brings the app's
                                // error dialog, whose re-render aborts every request in flight, this reload
                                // included: the list could never come back while the counts fail.
                                onClick={(): void => void store.loadPage()}
                            >
                                Retry
                            </BaseButton>
                        }
                    >
                        {store.error} — the list is unknown, not empty.
                    </S.PageBanner>
                )}
                {store.page?.truncated && (
                    // NS-3: a merged search that hit the API's page limit is a subset; its count is a floor.
                    <S.PageBanner tone="warning" title="Search results are incomplete">
                        One of the searched fields matched more than 100 records, so not every match is listed. Add more
                        of the name, NPI or address to narrow the search.
                    </S.PageBanner>
                )}
                <S.Panel>
                    {/* Keyed by tab: each tab mounts its own table, which asks for its own first page. */}
                    <RosterTable
                        key={store.tab}
                        store={store}
                        presenter={presenter}
                        getVisibleColumns={() =>
                            rosterVisibleColumns(presenter, store.tab, store.role, store.dataSource)
                        }
                    />
                </S.Panel>
                <RosterDrawer store={store} />
                <RosterClaimsDialog store={store} />
            </S.Wrapper>
        </RosterDialogContext.Provider>
    );
});
